import React, { useMemo } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput/ControlledPhoneInput'
import ControlledTextInput from 'transfix-form/components/ControlledTextInput'
import EmailIcon from 'transfix-icons/build/EmailIcon'
import PhoneIcon from 'transfix-icons/build/PhoneIcon'
import { Box, makeStyles } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useTeamMemberContext } from 'apps/fleet-planner/context/TeamMemberContext'
import { useTeamMemberFormContext } from 'apps/fleet-planner/context/TeamMemberFormContext'
import { MODAL_MODES } from 'apps/fleet-planner/pages/team/constants'
import { IMotorCarrierDetail, ITeamMember } from 'apps/fleet-planner/types'
import { Avatar, brandColors, Spinner, TextField } from 'transfix-ui'
import { hasNondriverUserType } from 'utils/teamMemberHelpers'
import { TeamMemberDetailFormTitle } from '../form/TeamMemberDetailFormTitle'
import { CarrierAccessList } from './CarrierAccessList'
import MobilePhoneAndStatusUpdatesSubform from './MobilePhoneAndStatusUpdatesSubform'
import { TeamMemberAutocomplete } from './TeamMemberAutocomplete'

const useCaptionStyles = makeStyles(({ breakpoints }) => ({
  text: {
    [breakpoints.down('sm')]: {
      color: brandColors.coolGray6,
    },
  },
}))

export const TeamMemberDetailForm = () => {
  const { isMobile } = useBreakpointContext()
  const {
    isFetching,
    modalMode,
    selectedTeamMember,
    setModalMode,
    setSelectedTeamMember,
  } = useTeamMemberContext()
  const { control, errors, watch } = useTeamMemberFormContext()
  const motorCarrierDetails = useWatch<IMotorCarrierDetail[]>({
    name: 'motorCarrierDetails',
    control,
    defaultValue: [],
  })

  const isDispatcherOrOwnerOperator = useMemo(() => hasNondriverUserType(motorCarrierDetails), [
    motorCarrierDetails,
  ])

  const searchOnChange = (selected?: Partial<ITeamMember>) => {
    const teamMember = selected ? (selected as ITeamMember) : undefined
    setSelectedTeamMember(teamMember)
    setModalMode(MODAL_MODES.ADD_EXISTING)
  }

  const firstName = watch('firstName') || '_'
  const lastName = watch('lastName') || '_'

  const isAddExistingMode = modalMode === MODAL_MODES.ADD_EXISTING
  const isEditExistingMode = modalMode === MODAL_MODES.EDIT_EXISTING
  const isExisitingMode = isAddExistingMode || isEditExistingMode

  const { cellPhone, email, phone } = selectedTeamMember || {}

  const isExistingUserWithValidEmail = isExisitingMode && !!email
  const isAddingExistingWithoutSelected = isAddExistingMode && !selectedTeamMember

  const isDisabledEmailInput =
    isDispatcherOrOwnerOperator && (isExistingUserWithValidEmail || isAddingExistingWithoutSelected)

  const isAddingExisitingWithPhone = isAddExistingMode && !!phone
  const isValidPhone = !!phone && phone?.length === 10
  const isDisabledPhoneInput =
    (isValidPhone && isAddingExisitingWithPhone) || isAddingExistingWithoutSelected

  const isAddExisitingWithCellPhone = isAddExistingMode && !!cellPhone
  const isValidCellPhone = !!cellPhone && cellPhone?.length === 10
  const isDisabledCellPhoneInput =
    isValidCellPhone && (isAddExisitingWithCellPhone || isAddingExistingWithoutSelected)

  const captionClasses = useCaptionStyles()

  return (
    <Box display='grid' gap={2} mb={4}>
      {isAddExistingMode ? (
        <>
          <TeamMemberAutocomplete value={undefined} onChange={searchOnChange} />
          <ControlledHiddenInput control={control} name='firstName' />
          <ControlledHiddenInput control={control} name='lastName' />
        </>
      ) : (
        <>
          {isMobile && (
            <Box mb={-2}>
              <TeamMemberDetailFormTitle
                leftComponent={<Avatar name={`${firstName} ${lastName}`} size='xsmall' />}
                hidePaddingTop>
                Name
              </TeamMemberDetailFormTitle>
            </Box>
          )}
          <ControlledTextInput
            control={control}
            name='firstName'
            TextInputProps={{
              placeholder: 'John',
              label: 'First Name',
              large: isMobile,
              autoFocus: true,
              validationMessage: errors?.firstName?.message,
              error: !!errors?.firstName?.message,
              dataTest: 'addTeamMember-firstName-input',
            }}
          />
          <ControlledTextInput
            control={control}
            name='lastName'
            TextInputProps={{
              placeholder: 'Doe',
              label: 'Last Name',
              large: isMobile,
              validationMessage: errors?.lastName?.message,
              error: !!errors?.lastName?.message,
              dataTest: 'addTeamMember-lastName-input',
            }}
          />
        </>
      )}
      <h3>Carriers they have access to</h3>
      <small className={captionClasses.text}>
        You’ll be able to assign your team member to any of the loads belonging to the carriers you
        select below. To add a carrier to this list, you can add a new carrier from your Carriers
        tab.
      </small>
      <Box overflow='scroll' mb={2}>
        {isFetching ? <Spinner /> : <CarrierAccessList />}
      </Box>
      {/* Contact email title */}
      {isMobile && (
        <Box mb={-2}>
          <TeamMemberDetailFormTitle leftComponent={<EmailIcon size='large' />}>
            Contact Email
          </TeamMemberDetailFormTitle>
        </Box>
      )}
      <Controller
        control={control}
        name='email'
        render={({ onBlur, onChange, value }) => (
          <TextField
            required={isDispatcherOrOwnerOperator && !isDisabledEmailInput}
            disabled={isDisabledEmailInput}
            placeholder=''
            label={isDispatcherOrOwnerOperator ? 'Email' : 'Contact Email (Optional)'}
            large={isMobile}
            validationMessage={errors?.email?.message}
            error={!!errors?.email?.message}
            dataTest='addTeamMember-email-input'
            helperMessage={
              isDispatcherOrOwnerOperator
                ? 'This is the email your team member will use to log in to Transfix.'
                : ''
            }
            clearable={!isDisabledEmailInput}
            className='fs-mask'
            onBlur={onBlur}
            onChange={onChange}
            value={value}
          />
        )}
      />
      {isMobile && (
        <Box mb={-2}>
          <TeamMemberDetailFormTitle leftComponent={<PhoneIcon size='large' />}>
            Phone Numbers
          </TeamMemberDetailFormTitle>
        </Box>
      )}
      {isDispatcherOrOwnerOperator && (
        <ControlledPhoneInput
          control={control}
          name='phone'
          PhoneInputProps={{
            required: !isDisabledPhoneInput,
            disabled: isDisabledPhoneInput,
            placeholder: '(555) 123-4567',
            label: 'Phone',
            large: isMobile,
            invalid: !!errors?.phone?.message,
            dataTest: 'addTeamMember-phone-input',
            validationMessage:
              errors?.phone?.message ||
              'This phone number will be shown to customers only when this team member is assigned to the customer’s load.',
            clearable: !isDisabledPhoneInput,
            className: 'fs-mask',
          }}
        />
      )}
      <MobilePhoneAndStatusUpdatesSubform
        phoneInput={
          <ControlledPhoneInput
            control={control}
            name='cellPhone'
            PhoneInputProps={{
              required: !isDisabledCellPhoneInput,
              disabled: isDisabledCellPhoneInput,
              placeholder: '(555) 123-4567',
              label: 'Mobile Phone',
              large: isMobile,
              invalid: !!errors?.cellPhone?.message,
              validationMessage:
                errors?.cellPhone?.message || !isDispatcherOrOwnerOperator
                  ? 'This is the number your team member will use to log in to Transfix.'
                  : '',
              dataTest: 'addTeamMember-cellphone-input',
              clearable: !isDisabledCellPhoneInput,
              className: 'fs-mask',
            }}
          />
        }
      />
    </Box>
  )
}
